import React, { useEffect, useState } from "react";
import { Loader2, X, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { affiliatesAdminApi } from "../../lib/api";
import { CustomerStatusTable, PendingPeriodCard, PayrollHistoryList, money, fmtDate } from "../../components/affiliate/PayrollViews";

const KIND_LABEL = { first_purchase: "Subscription", subscription: "Subscription", credits: "AI credits", residual: "Residual renewal" };

/** Read-only admin view — mirrors exactly what the affiliate sees in their own
 * portal (customers, pending payroll, payroll history, commission ledger).
 * Payroll is still processed from the Platform Console Payroll section. */
export default function AffiliateDetailModal({ affiliateId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    affiliatesAdminApi.detail(affiliateId)
      .then(setData)
      .catch(() => toast.error("Could not load affiliate detail."))
      .finally(() => setLoading(false));
  }, [affiliateId]);

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="affiliate-detail-modal">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0F1629] border border-white/10 shadow-2xl" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0F1629] z-10">
          <h3 className="text-[15px] font-bold text-slate-100 inline-flex items-center gap-2">
            <HandCoins className="h-4 w-4 text-amber-400" /> {data?.affiliate?.name || "Affiliate"} <span className="text-[11px] font-normal text-slate-500 normal-case">— read-only admin view</span>
          </h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400"><X className="h-4 w-4" /></button>
        </div>
        {loading || !data ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 text-cyan-400 animate-spin" /></div>
        ) : (
          <div className="p-5 space-y-6">
            <CustomerStatusTable customers={data.customers} testid="affiliate-detail-customers" />
            <PendingPeriodCard period={data.pendingPeriod} testid="affiliate-detail-pending-period" />
            <PayrollHistoryList history={data.payoutHistory} testid="affiliate-detail-payroll-history" />

            <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="affiliate-detail-ledger">
              <div className="px-5 py-3.5 border-b border-white/10"><h3 className="text-[14px] font-bold text-slate-100">Full commission ledger</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[13px] min-w-[760px]">
                  <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
                    <tr>
                      <th className="px-5 py-2.5 font-semibold">Date</th>
                      <th className="px-3 py-2.5 font-semibold">Customer</th>
                      <th className="px-3 py-2.5 font-semibold">Type</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Gross</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Commission</th>
                      <th className="px-3 py-2.5 font-semibold">Payout period</th>
                      <th className="px-5 py-2.5 font-semibold text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {(data.ledger || []).length === 0 ? (
                      <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500">No commissions logged yet.</td></tr>
                    ) : data.ledger.map((l) => (
                      <tr key={l.id} className="text-slate-200" data-testid={`affiliate-detail-ledger-row-${l.id}`}>
                        <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{fmtDate(l.createdAt)}</td>
                        <td className="px-3 py-3">{l.companyName}{l.splitPart && <span className="ml-1.5 text-[10.5px] text-amber-300/90 font-semibold">{l.splitPart === "advance" ? "Annual Advance" : "Annual Balance"}</span>}</td>
                        <td className="px-3 py-3 text-slate-300">{KIND_LABEL[l.kind] || l.kind}</td>
                        <td className="px-3 py-3 text-right tabular-nums text-slate-400">{money(l.amountPaid)}</td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-emerald-300">{money(l.commissionAmount)}</td>
                        <td className="px-3 py-3 text-slate-400 whitespace-nowrap">{l.payoutFriday}</td>
                        <td className="px-5 py-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${l.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : l.status === "held" ? "bg-sky-500/15 text-sky-300" : "bg-amber-500/15 text-amber-300"}`}>{l.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
